"use client";

import dynamic from "next/dynamic";
import { Suspense, useEffect, type ReactNode } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { captureUtms } from "@/utils/utm";
import patchDomReconciliation from "@/utils/patchDomReconciliation";
import { DEFAULT_LOCALE } from "@/utils/localeConstants";
import {
  ReadableCopy,
  ReadableValue,
  readableFallback,
} from "@/components/readable";

patchDomReconciliation();

// The placeholder lets globals.scss reserve the fixed nav's space from the
// first paint, so the page does not drop when the nav arrives.
const PrimaryNavigation = dynamic(
  () => import("@/templates/PrimaryNavigation"),
  { ssr: false, loading: readableFallback("nav-placeholder") },
);

const Footer = dynamic(() => import("@/templates/Footer"), {
  ssr: false,
  loading: readableFallback(),
});

interface ClientLayoutProps {
  navData?: any;
  footerData?: any;
  locale?: string;
  children: ReactNode;
}

// Picks up UTMs from navigations that only change the query string. It sits in
// its own Suspense boundary because useSearchParams would otherwise stop pages
// from being prerendered.
function UtmQueryCapture() {
  const searchParams = useSearchParams();

  useEffect(() => {
    captureUtms();
  }, [searchParams]);

  return null;
}

export default function ClientLayout({
  navData,
  footerData,
  locale,
  children,
}: ClientLayoutProps) {
  const pathname = usePathname();

  // Runs in the same commit as the page, before useMounted's follow-up render,
  // so forms always see the UTMs from the landing URL.
  useEffect(() => {
    captureUtms();
  }, [pathname]);

  const readableLocale = locale ?? DEFAULT_LOCALE;

  return (
    <>
      <Suspense fallback={null}>
        <UtmQueryCapture />
      </Suspense>
      <ReadableCopy
        copy={
          <nav>
            <ReadableValue value={navData} locale={readableLocale} />
          </nav>
        }
      >
        <PrimaryNavigation data={navData} locale={locale} />
      </ReadableCopy>
      {children}
      <ReadableCopy
        copy={
          <footer>
            <ReadableValue value={footerData} locale={readableLocale} />
          </footer>
        }
      >
        <Footer data={footerData} locale={locale} />
      </ReadableCopy>
    </>
  );
}
